import {
  IProcessedEvent,
  SyncOrAsyncOrFn,
  ToolbarButtonDefinition,
  XuxGridActionPayload,
} from '../index';
import { Observable, BehaviorSubject } from '@shared/rx';
import { SchemaNode } from '../';
import { ENTITY_TYPES } from '../enums';
import { IBaseFormService } from './form-service.type';
import { GridOptions } from 'ag-grid-community';
import {
  IFormRules,
  IWidgetData,
} from '../../../components/form/services/schema-form/schema-form-interfaces';
import { ComponentRef } from '@angular/core';
import { MODAL_CONTAINER_KEYS } from '@app/containers/container-keys';
import { MODAL_COMPONENT_KEYS } from '@app/containers/component-keys';
import { AsyncValidatorFn } from '@angular/forms';
import { XVII18nService } from '@app/i18n/services';

export type ModalMapper = <T>(value: T) => T;
export type ModalWithPayloadMapper = <T>(
  value: T,
  payload: XuxGridActionPayload,
) => T;

export interface IModalMapperCollection {
  /**
   * functions which are used to modify a value representation for the modal according to
   * the action (create\edit\delete etc.)
   */
  beforeActionMappers?: ModalWithPayloadMapper[];
  /**
   * functions which are used to modify a value representation after the modal is closed with
   * according to the action (create\edit\delete etc.)
   */
  resultMappers?: ModalMapper[];
}

export interface IModalMappers {
  edit?: IModalMapperCollection;
}

/**
 * Notice: `formValue` argument is the modal form value
 */
export type ModalDynamicOptionValue<T = string> = SyncOrAsyncOrFn<
  T,
  (formValue: object) => T
>;

export interface IInlineLabelOptions {
  widgetData: IWidgetData;
}

export interface IModalDialogOptions {
  modalComp?: MODAL_COMPONENT_KEYS;
  modalType?: string;
  hideSidebar?: boolean;
  modalClass?: string;
  ownDocumentName?: string;
  title?: ModalDynamicOptionValue;
  titleObs?: BehaviorSubject<string> | Observable<string>;
  subTitle?: ModalDynamicOptionValue;
  subTitleObs?: BehaviorSubject<string> | Observable<string>;
  text?: string;
  label?: string;
  value?: any;
  buttons?: ToolbarButtonDefinition[];
  validation?: boolean;
  validationMessageShow?: boolean;
  showPristineError?: boolean;
  schema?: any;
  focus?: boolean;
  ignoreBackdropClick?: boolean;
  schemaObs?: Observable<SchemaNode>;
  schemaMapper?: (schema: SchemaNode) => SchemaNode;
  formRules?: IFormRules;
  onFormReady?: Function;
  onFormClose?: Function;
  beforeFormClose?: Function;
  onFormAction?: Function;
  icon?: string;
  visible?: boolean;
  modalId?: string;
  defaultWidgetFamily?: string;
  animated?: boolean;
  formService?: IBaseFormService<any>;
  entityType?: ENTITY_TYPES;
  modalMappers?: IModalMappers;
  modalResultMapper?: ModalMapper;
  isScrollable?: boolean;
  stopEditingWhenGridLosesFocus?: boolean;
  populateSearchValue?: IPopulateSearchValueDefinition;
  userGridOptions?: GridOptions;
  setupEmitWidgetEvent?: Function;
  buttonCaptions?: {
    saveAndClose?: string;
    saveAndAssign?: string;
  };
  showLoader?: boolean;
  autoCloseModal?: boolean;
  inlineLabelObs?: Observable<IInlineLabelOptions[]>;
  needEventListener?: boolean;

  contentComp?: MODAL_CONTAINER_KEYS;
  onContentComp?: {
    created?: (compRef: ComponentRef<any>, containerRef: IContainerRef) => void;
  };
}

export interface IPrintOpenSheetOptions {
  eventList: IProcessedEvent[];
  validator: AsyncValidatorFn;
  i18nService: XVII18nService;
}

export interface IContainerRef {
  destroyFunc?: Function;
  reloadFunc?: Function;
}

export type modalResult = 'ok' | 'cancel' | 'none' | 'submit' | 'back';

export interface IModalDialogResult {
  modalResult?: modalResult;
  modalId?: string | number;
  confirmed?: boolean;
  value?: any;
  items?: any[];
  [key: string]: any;
}

export interface IDropdownMenuOption {
  title: string;
  value: any;
  disabled?: boolean;
  /**
   * used for making option as a simple string without extra styles which are applied by default
   * e.g. for supporting read only mode by dropdown
   */
  isSimpleItem?: boolean;
}

export interface IDropdownMenuOptions {
  x?: number;
  y?: number;
  options?: IDropdownMenuOption[];
  directionCss?: {
    xStyle?: string;
    yStyle?: string;
    wrapperClass?: string;
  };
}

export enum DROPDOWN_MENU_WRAPPER_CLASSES {
  IN_MODAL = 'xvi-dropdown-menu-inside-modal',
}

export interface IPopulateSearchValueDefinition {
  destinationFieldName: string;
  destinationValueMapper?: Function;
}

export enum SORT_ORDERS {
  ASC = 'asc',
  DESC = 'desc',
}

export const DEFAULT_SORTING_ORDERS = [null, SORT_ORDERS.ASC, SORT_ORDERS.DESC];

export type ModalAction = 'create' | 'edit' | 'remove' | 'add';

export enum ACTION_PLACE {
  EDIT_FORM = 'edit_form',
  GRID_LIST = 'grid_list',
}

export enum USER_ACCESS {
  FULL = 'full',
  PARTICULAR = 'particular',
}

export interface IDeleteConfirmationMessageOptionsList {
  place: ACTION_PLACE;
  access?: USER_ACCESS;
}

export interface PromptSiteSelectorOptions {
  required?: boolean;
  applySiteSelection?: boolean;
  hideBackButton?: boolean;
  disableHistoryBack?: boolean;
  isMultiSiteAvailable?: boolean;
}
